import React from 'react';
import { useMutation } from 'urql';
import { useForm } from 'react-hook-form';
import { tw } from 'twind/style';
import {
  Container,
  Heading,
  InputSelect,
  InputText,
  InputTextArea,
  Stack,
  Button,
} from '@global-ecom/nitro-uds/elements';

import { ManagedHeading } from 'ui/components/ManagedHeading';
import { useGiftCardQuery } from '__generated__/graphql';
import { Page } from 'ui/components/Page';
import { Breadcrumb } from 'ui/components/Breadcrumb';
import { useTranslate } from 'hooks/useTranslations';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { useValidation } from 'hooks/useValidation';
import { useMiniCart } from 'hooks/useMiniCart';
import { MiniCart } from 'ui/components/MiniCart';
import { PageHead } from 'ui/components/PageHead';
import { ADD_GIFTCARD_TO_CART_MUTATION } from 'gql/mutations/cart';
import { useCart } from 'hooks/useCart';

const VirtualGiftCard: React.FC = () => {
  const t = useTranslate();
  const [validate] = useValidation();
  const { currency } = useSiteConfig();

  const [query] = useGiftCardQuery();
  const giftCard = query.data?.giftCard;

  const path = [
    { label: 'home', url: '' },
    { label: t('giftCard'), url: '' },
  ];

  type DigitalGiftCardFields = {
    value: number | string;
    customValue?: number;
    recipientName: string;
    yourName: string;
    recipientEmail: string;
    confirmEmail: string;
    giftMessage?: string;
  };

  const [addGiftCardResult, addGiftCard] = useMutation(
    ADD_GIFTCARD_TO_CART_MUTATION
  );
  const { isMiniCartVisible, openMiniCart } = useMiniCart();

  const { cart } = useCart();

  const { register, handleSubmit, watch, errors, formState, reset } =
    useForm<DigitalGiftCardFields>({
      defaultValues: {
        ...(giftCard?.defaultValue && { value: giftCard.defaultValue }),
      },
      mode: 'onChange',
    });

  const onSubmit = async (data: DigitalGiftCardFields) => {
    const { customValue, ...orderData } = data;
    if (orderData.value === 'custom') orderData.value = customValue!;

    const addGiftCardResult = await addGiftCard({
      input: {
        amount: Number(orderData.value),
        recipientName: orderData.recipientName,
        recipientEmail: orderData.recipientEmail,
        senderName: orderData.yourName,
        message: orderData.giftMessage,
        basketId: cart?.id,
      },
    });

    const productsInCart = addGiftCardResult.data?.addGiftCardToCart.products;

    const giftCardsInCart =
      productsInCart?.filter(
        product => product.__typename === 'GiftCardCartItem'
      ) || [];

    if (giftCardsInCart?.length > 0) {
      const latestGiftCard = giftCardsInCart[giftCardsInCart.length - 1];
      openMiniCart(latestGiftCard);
    }

    reset();
  };

  const showCustomValueField = watch('value') === 'custom';

  return (
    <Page query={query}>
      <PageHead
        title={t('pageTitleReduced', {
          title: t('digitalGiftCard'),
        })}
      />
      {!query.fetching && giftCard && (
        <React.Fragment>
          <Breadcrumb path={path} />
          <Container
            size="lg"
            className=" grid grid-cols-1 md:grid-cols-12 gap-10 items-start pb-6"
          >
            <div
              className="md:col-span-6 xl:pr-6 h-full relative"
              style={{ paddingTop: '85%' }}
            >
              <img
                src={giftCard.image}
                alt={t('giftCard')}
                width="1000"
                height="1000"
                className="mb-4 absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <Stack gap="xl" className="md:col-span-6 lg:pr-6">
              <Heading size="xl">
                <ManagedHeading tag="h1">{t('digitalGiftCard')}</ManagedHeading>
              </Heading>
              <Stack asChild>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <div>
                    <div
                      className={tw(
                        'grid gap-3',
                        showCustomValueField ? 'grid-cols-2' : 'grid-cols-1'
                      )}
                    >
                      <InputSelect
                        id="select-gift-card-value"
                        name="value"
                        placeholder={t('selectValue')}
                        label={t('selectValue')}
                        options={[
                          ...giftCard.valueOptions.map(value => ({
                            value: `${value}`,
                            label: `${currency.symbol}${value}.00`,
                          })),
                          { value: 'custom', label: t('custom') },
                        ]}
                        defaultValue={watch('value')}
                        errorText={errors.value?.message}
                        required
                        ref={register({
                          required: t('requiredField'),
                          validate: value => {
                            if (value === 'placeholder')
                              return t('requiredField');
                            return undefined;
                          },
                        })}
                      />
                      {showCustomValueField && (
                        <InputText
                          id="customValue"
                          name="customValue"
                          label={t('setValue')}
                          placeholder={t('setValue')}
                          type="number"
                          step={10}
                          min={10}
                          max={500}
                          required
                          ref={register({
                            required: t('requiredField'),
                            pattern: {
                              value: /^\d+\.?\d*$/,
                              message: t('giftCardValueFormat'),
                            },
                            validate: value => {
                              const customValue = Number(value);
                              if (customValue < 10 || customValue > 500)
                                return t('giftCardValueRange');
                              if (customValue % 10)
                                return t('giftCardValueMultiple');
                            },
                          })}
                          errorText={errors?.customValue?.message}
                          crossOrigin={undefined}
                        />
                      )}
                    </div>
                    {/*  Handle error outside the Input component as the 2-col UI can get mucky 🙃 */}
                    {errors?.customValue?.message && (
                      <p
                        id="customValue_error"
                        className="text-xs text-puma-red self-start rounded-b leading-tight mt-1"
                      >
                        {errors.customValue.message}
                      </p>
                    )}
                  </div>
                  <InputText
                    id="recipientName"
                    name="recipientName"
                    placeholder={t('recipientName')}
                    label={t('recipientName')}
                    required
                    ref={register({
                      required: t('requiredField'),
                      validate: validate.safeInput,
                      maxLength: {
                        value: 100,
                        message: t<'genericFieldMaxLength'>(
                          'genericFieldMaxLength',
                          {
                            length: 100,
                          }
                        ),
                      },
                    })}
                    errorText={errors.recipientName?.message}
                    crossOrigin={undefined}
                  />
                  <InputText
                    id="yourName"
                    name="yourName"
                    placeholder={t('yourName')}
                    label={t('yourName')}
                    required
                    ref={register({
                      required: t('requiredField'),
                      validate: validate.safeInput,
                      maxLength: {
                        value: 100,
                        message: t<'genericFieldMaxLength'>(
                          'genericFieldMaxLength',
                          {
                            length: 100,
                          }
                        ),
                      },
                    })}
                    errorText={errors.yourName?.message}
                    crossOrigin={undefined}
                  />
                  <InputText
                    id="recipientEmail"
                    name="recipientEmail"
                    placeholder={t('recipientEmail')}
                    label={t('recipientEmail')}
                    type="email"
                    required
                    ref={register({
                      required: t('requiredField'),
                      validate: validate.email,
                      maxLength: {
                        value: 50,
                        message: t<'genericFieldMaxLength'>(
                          'genericFieldMaxLength',
                          {
                            length: 50,
                          }
                        ),
                      },
                    })}
                    errorText={errors.recipientEmail?.message}
                    crossOrigin={undefined}
                  />
                  <InputText
                    id="confirmEmail"
                    name="confirmEmail"
                    placeholder={t('confirmEmail')}
                    label={t('confirmEmail')}
                    type="email"
                    required
                    ref={register({
                      required: t('requiredField'),
                      validate: value =>
                        value !== watch('recipientEmail')
                          ? t('emailsDontMatch')
                          : undefined,
                      maxLength: {
                        value: 50,
                        message: t<'genericFieldMaxLength'>(
                          'genericFieldMaxLength',
                          {
                            length: 50,
                          }
                        ),
                      },
                    })}
                    errorText={errors.confirmEmail?.message}
                    crossOrigin={undefined}
                  />
                  <InputTextArea
                    id="giftMessage"
                    name="giftMessage"
                    label={t('message')}
                    placeholder={t('message')}
                    size="base"
                    ref={register({
                      validate: validate.safeInput,
                      maxLength: {
                        value: 300,
                        message: t<'genericFieldMaxLength'>(
                          'genericFieldMaxLength',
                          {
                            length: 300,
                          }
                        ),
                      },
                    })}
                    rows={6}
                    maxLength={300}
                    errorText={errors.giftMessage?.message}
                  />
                  <Button
                    loading={addGiftCardResult.fetching}
                    dataTestId="gift-card-add-to-cart"
                    label={t('addToCart')}
                    type="submit"
                    disabled={!formState.isValid || addGiftCardResult.fetching}
                    icon="cart"
                  />
                </form>
              </Stack>
            </Stack>
          </Container>
        </React.Fragment>
      )}
      {isMiniCartVisible && <MiniCart />}
    </Page>
  );
};

export default VirtualGiftCard;
